import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { fmtIDR, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Cake, Wallet, AlertCircle, Send } from "lucide-react";

export default function Loyalty() {
  const { branchId } = useBranch();
  const [tab, setTab] = useState("deposit_active");
  const [active, setActive] = useState([]);
  const [empty, setEmpty] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/loyalty/deposit-active", { params: { branch_id: branchId } }),
      api.get("/loyalty/deposit-empty", { params: { branch_id: branchId } }),
      api.get("/loyalty/birthday", { params: { branch_id: branchId, window_days: 30 } }),
    ])
      .then(([a, e, b]) => {
        setActive(a.data);
        setEmpty(e.data);
        setBirthdays(b.data);
      })
      .finally(() => setLoading(false));
  }, [branchId]);

  const sendReminder = async (p, category) => {
    const subject = {
      deposit_remind: `Halo ${p.name}, saldo deposit Anda masih tersedia`,
      deposit_upsell: `Special offer: Upgrade deposit Anda untuk benefit lebih`,
      birthday: `Selamat ulang tahun ${p.name}! 🎉 Hadiah spesial menanti`,
    }[category];
    const body = {
      deposit_remind: `Saldo deposit ${fmtIDR(p.deposit_balance)} dapat Anda gunakan kapan saja. Booking sekarang untuk treatment favorit.`,
      deposit_upsell: `Nikmati cashback 10% saat Anda upgrade deposit ke level Gold (min Rp 10 jt).`,
      birthday: `Sebagai hadiah ulang tahun, kami sediakan voucher treatment senilai Rp 500.000.`,
    }[category];
    try {
      await api.post("/notifications", {
        patient_id: p.id,
        channel: p.email ? "email" : "wa",
        category,
        subject,
        body,
      });
      toast.success(`Notifikasi untuk ${p.name} ditambahkan ke outbox.`);
    } catch {
      toast.error("Gagal menambahkan notifikasi");
    }
  };

  return (
    <div className="space-y-6" data-testid="loyalty-page">
      <div>
        <h1 className="font-heading text-3xl tracking-tight">Notifikasi Loyalty Customer</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Tracking saldo deposit & birthday untuk aksi follow-up dan upselling.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-5 flex items-center gap-3">
            <span className="rounded-md bg-emerald-50 p-2.5 text-emerald-700">
              <Wallet className="h-4 w-4" />
            </span>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">
                Deposit Aktif
              </div>
              <div className="font-heading text-2xl tracking-tight">{active.length}</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 flex items-center gap-3">
            <span className="rounded-md bg-rose-50 p-2.5 text-rose-700">
              <AlertCircle className="h-4 w-4" />
            </span>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">
                Deposit Habis
              </div>
              <div className="font-heading text-2xl tracking-tight">{empty.length}</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 flex items-center gap-3">
            <span className="rounded-md bg-pink-50 p-2.5 text-pink-700">
              <Cake className="h-4 w-4" />
            </span>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">
                Ulang Tahun (30 hari)
              </div>
              <div className="font-heading text-2xl tracking-tight">{birthdays.length}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="loyalty-tabs">
          <TabsTrigger value="deposit_active" data-testid="tab-deposit-active">
            Deposit Aktif
          </TabsTrigger>
          <TabsTrigger value="deposit_empty" data-testid="tab-deposit-empty">
            Deposit Habis
          </TabsTrigger>
          <TabsTrigger value="birthday" data-testid="tab-birthday">
            Birthday Tracking
          </TabsTrigger>
        </TabsList>

        <TabsContent value="deposit_active">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Pasien dengan Saldo Deposit</CardTitle>
              <CardDescription>
                Follow up agar saldo dipakai. Ide: paket bundling treatment.
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pasien</TableHead>
                    <TableHead>Kontak</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead className="text-right">Deposit Awal</TableHead>
                    <TableHead>Last Visit</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {active.slice(0, 200).map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="text-sm font-medium">{p.name}</TableCell>
                      <TableCell className="text-xs">
                        <div>{p.phone}</div>
                        <div className="text-muted-foreground">{p.email}</div>
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium text-emerald-700">
                        {fmtIDR(p.deposit_balance)}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {fmtIDR(p.deposit_initial)}
                      </TableCell>
                      <TableCell className="text-xs">{fmtDate(p.last_visit_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`remind-active-${p.id}`}
                          onClick={() => sendReminder(p, "deposit_remind")}
                        >
                          <Send className="h-3.5 w-3.5 mr-1.5" /> Reminder
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deposit_empty">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Pasien Deposit Habis</CardTitle>
              <CardDescription>Peluang upselling ke level deposit lebih tinggi.</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pasien</TableHead>
                    <TableHead>Kontak</TableHead>
                    <TableHead className="text-right">Deposit Pernah</TableHead>
                    <TableHead>Last Visit</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {empty.slice(0, 200).map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="text-sm font-medium">{p.name}</TableCell>
                      <TableCell className="text-xs">
                        <div>{p.phone}</div>
                        <div className="text-muted-foreground">{p.email}</div>
                      </TableCell>
                      <TableCell className="text-right text-xs">{fmtIDR(p.deposit_initial)}</TableCell>
                      <TableCell className="text-xs">{fmtDate(p.last_visit_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`upsell-empty-${p.id}`}
                          onClick={() => sendReminder(p, "deposit_upsell")}
                        >
                          <Send className="h-3.5 w-3.5 mr-1.5" /> Upsell
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="birthday">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Birthday Tracking 30 Hari ke Depan</CardTitle>
              <CardDescription>
                Otomatis terdeteksi — kirim promo birthday spesial dari sini.
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pasien</TableHead>
                    <TableHead>Tanggal Lahir</TableHead>
                    <TableHead>H-</TableHead>
                    <TableHead>Kontak</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {birthdays.slice(0, 200).map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="text-sm font-medium">{p.name}</TableCell>
                      <TableCell className="text-xs">{p.birthday}</TableCell>
                      <TableCell>
                        <span className="rounded-full bg-pink-100 px-2 py-0.5 text-xs font-medium text-pink-800">
                          {p.days_until_birthday === 0 ? "Hari ini 🎉" : `H-${p.days_until_birthday}`}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{p.phone}</div>
                        <div className="text-muted-foreground">{p.email}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`birthday-send-${p.id}`}
                          onClick={() => sendReminder(p, "birthday")}
                        >
                          <Send className="h-3.5 w-3.5 mr-1.5" /> Kirim
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
